import { ChefHat } from "@/components/svg/ChefHat";
import {
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { MapPin, Check } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { motion } from "motion/react";

const options = [
  {
    value: "DOMICILE",
    icon: <ChefHat size={28} />,
    title: "Chef à domicile",
    description: "Pour une soirée ou un repas ponctuel — anniversaire, dîner en amoureux, réception.",
    tag: "Le plus populaire",
  },
  {
    value: "DEMEURE",
    icon: <MapPin size={28} />,
    title: "Chef à demeure",
    description: "Pour plusieurs jours — vacances en villa, résidence secondaire, séjour prolongé.",
    tag: "Idéal pour les séjours",
  },
];

export const Step1ChefType = () => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name="chefType"
      render={({ field }) => (
        <FormItem className="space-y-6 w-full">
          <div className="flex flex-col gap-1 mb-2">
            <h1 className="font-h2 text-dark-blue">Quel type de prestation ?</h1>
            <p className="font-p text-muted-foreground">
              Choisissez le format qui correspond à votre occasion.
            </p>
          </div>

          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              value={field.value}
              className="flex flex-col gap-3"
            >
              {options.map((opt) => {
                const selected = field.value === opt.value;
                return (
                  <motion.label
                    key={opt.value}
                    whileHover={{ scale: 1.005 }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className={cn(
                      "relative flex items-start gap-5 p-5 rounded-2xl border-2 cursor-pointer transition-[border-color,background,box-shadow] duration-200",
                      selected
                        ? "border-primary bg-primary/5 shadow-sm shadow-primary/10"
                        : "border-border bg-white hover:border-gray-300 hover:shadow-sm"
                    )}
                  >
                    <input
                      type="radio"
                      value={opt.value}
                      checked={selected}
                      onChange={() => field.onChange(opt.value)}
                      className="sr-only"
                    />

                    {/* Icon */}
                    <motion.div
                      animate={selected ? { rotate: [0, -8, 8, 0] } : { rotate: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className={cn(
                        "shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-colors",
                        selected ? "bg-primary text-white" : "bg-gray-100 text-gray-500"
                      )}
                    >
                      {opt.icon}
                    </motion.div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-h4 text-dark-blue">{opt.title}</h3>
                        <span className={cn(
                          "text-xs font-semibold px-2.5 py-0.5 rounded-full",
                          selected ? "bg-primary/15 text-primary" : "bg-gray-100 text-gray-500"
                        )}>
                          {opt.tag}
                        </span>
                      </div>
                      <p className="font-small text-muted-foreground leading-relaxed">
                        {opt.description}
                      </p>
                    </div>

                    {/* Check mark */}
                    <div className={cn(
                      "shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all",
                      selected ? "border-primary bg-primary" : "border-gray-300"
                    )}>
                      {selected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500, damping: 25 }}
                        >
                          <Check size={12} strokeWidth={3} className="text-white" />
                        </motion.div>
                      )}
                    </div>
                  </motion.label>
                );
              })}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
